import React, { useState, useEffect } from 'react';
import * as LucideIcons from 'lucide-react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../AppContext';

const NoSpendStreakTracker = () => {
  const { t } = useTranslation();
  const { userId, db, appId } = useAppContext();
  const [streak, setStreak] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!userId || !db) return;

    const transQuery = query(
      collection(db, `artifacts/${appId}/users/${userId}/transactions`),
      where("amount", "<", 0),
      orderBy("amount"),
      orderBy("timestamp", "desc")
    );

    const unsubscribe = onSnapshot(transQuery, (snapshot) => {
      // Map docs to data and filter out any transactions that don't have a valid Firestore timestamp
      const spendingTransactions = snapshot.docs
        .map(doc => doc.data())
        .filter(tx => tx && tx.timestamp && typeof tx.timestamp.toDate === 'function');

      if (spendingTransactions.length === 0) {
        setStreak(0);
        setIsLoading(false);
        return;
      }

      // Logic to calculate the no-spend streak
      let currentStreak = 0;
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const uniqueSpendDays = new Set();
      spendingTransactions.forEach(tx => {
        // Guarded usage: timestamp is guaranteed to have toDate() because of the filter above
        const txDate = tx.timestamp.toDate();
        txDate.setHours(0, 0, 0, 0);
        uniqueSpendDays.add(txDate.getTime());
      });

      // Check from yesterday backwards
      let dayToCheck = new Date(today);
      dayToCheck.setDate(dayToCheck.getDate() - 1);

      while (true) {
        if (uniqueSpendDays.has(dayToCheck.getTime())) {
          // Found a spend day, so the streak ends here
          break;
        } else {
          // No spending on this day, increment streak
          currentStreak++;
        }
        // Move to the previous day
        dayToCheck.setDate(dayToCheck.getDate() - 1);
      }

      // Also check if today has any spending
      if (uniqueSpendDays.has(today.getTime())) {
        // If there was spending today, the streak is 0
        setStreak(0);
      } else {
        setStreak(currentStreak);
      }

      setIsLoading(false);
    }, (error) => {
      console.error("Error fetching transactions for streak: ", error);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [userId, db, appId]);

  if (isLoading) {
    return (
        <div className="bg-gray-800/50 p-3 rounded-lg flex items-center justify-center animate-pulse">
            <LucideIcons.Sparkles size={20} className="mr-2 text-cyan-400" />
            <p className="text-sm text-gray-400">{t('noSpendStreak.calculating')}</p>
        </div>
    );
  }

  if (streak === 0) {
    return null; // Don't show the component if there is no streak
  }

  return (
    <div className="bg-gradient-to-r from-cyan-900/50 to-blue-900/50 p-3 rounded-lg flex items-center justify-center shadow-lg border border-cyan-700/50 mb-6">
        <LucideIcons.Snowflake size={24} className="mr-3 text-cyan-300 animate-pulse-slow" />
        <div>
            <p className="text-lg font-bold text-white">{t('noSpendStreak.streak', { count: streak })}</p>
            <p className="text-xs text-cyan-200">{t('noSpendStreak.encouragement')}</p>
        </div>
    </div>
  );
};

export default NoSpendStreakTracker;
